import type {DashboardColumnKey} from '../constants/dashboardColumns';
import type {DashboardDateField} from '../hooks/useDashboardFilters';
import type {DashboardFilterState} from '../hooks/useDashboardFilterPanel';

const DATE_FIELD_SLUGS: Record<DashboardDateField, string> = {
	created_at: 'created',
	last_activity_at: 'last-activity',
	resolved_at: 'resolved',
};

/**
 * The export route on the Forecast origin — same-origin, so the session cookie
 * rides along, and permission-gated there.
 *
 * Deliberately not the ticketing gateway: it proxies only `/internal/*`, `/api`
 * and `/healthz`, so any other path falls through to its SPA fallback and answers
 * 200 `text/html`, which is how a CSV download silently becomes an HTML file.
 */
export const TICKET_EXPORT_PATH = '/ticketing-ticket-export.csv';

export function dashboardCsvFilename({from, to, dateField}: {from: string; to: string; dateField: DashboardDateField}): string {
	return `ticket-dashboard-${DATE_FIELD_SLUGS[dateField]}-${from}-to-${to}.csv`;
}

/**
 * Maps the dashboard's frontend column keys to the export endpoint's column
 * param names. Keys absent from this map have no backing export column and are
 * silently dropped rather than forwarded — an entirely-unknown set would cause
 * the backend to fall back to its DEFAULT_COLUMNS, which would produce a file
 * whose columns bear no relation to what the user was looking at.
 */
export const EXPORT_COLUMN_KEY: Partial<Record<DashboardColumnKey, string>> = {
	id: 'id',
	subject: 'subject',
	requester: 'requester',
	assignee: 'assignee',
	status: 'status',
	priority: 'priority',
	channel: 'channel',
	created: 'created_at',
	resolution_time: 'resolution_time_seconds',
	first_response_time: 'first_response_time_seconds',
	last_activity_at: 'last_activity_at',
	resolved_at: 'resolved_at',
	watchers_count: 'watchers_count',
	// Unmapped (no backing export column): contact, project, tags, sla_state,
	// sla_due_at, sla_breached_at, sla_paused_at, time_in_current_status,
	// sla_policy_name
};

/**
 * Converts an ordered list of visible frontend column keys into the ordered
 * export column param values, dropping any keys that have no export equivalent.
 */
export function toExportColumns(columnKeys: readonly DashboardColumnKey[]): string[] {
	const result: string[] = [];
	for (const key of columnKeys) {
		const exportKey = EXPORT_COLUMN_KEY[key];
		if (exportKey !== undefined) {
			result.push(exportKey);
		}
	}
	return result;
}

/**
 * Builds the URLSearchParams for the export endpoint, mirroring the param names
 * that the backend's shared ticket-filter parser reads. Each array field is
 * appended as repeated params (never comma-joined) because that parser does not
 * split on commas: it validates each entry against the status/priority/channel
 * vocabulary and drops what it does not recognise, so `status=open,pending`
 * would leave the export with no status filter at all — a file containing
 * tickets the table on screen excluded, with nothing reporting an error.
 *
 * The fields serialized here are exactly the ones the table's own query sends
 * (see `buildDashboardTicketFilters`), so the file can never describe a
 * different set than the table it was taken from. The filter panel's
 * last-activity, resolved-date and first-response ranges are therefore
 * deliberately NOT sent: the table query does not send them either, and
 * applying them here alone would hand the user a file with fewer rows than the
 * table they exported it from. They belong in both places or neither.
 *
 * The optional `filename` param is forwarded so the server can set a
 * descriptive Content-Disposition name carrying the range and date field.
 */
export function buildExportParams(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField,
	columnKeys: readonly DashboardColumnKey[],
	filename?: string
): URLSearchParams {
	const params = new URLSearchParams();

	for (const v of applied.status) params.append('status', v);
	for (const v of applied.priority) params.append('priority', v);
	for (const v of applied.channel) params.append('channel', v);
	for (const v of applied.assignee) params.append('assignee_id', v);
	for (const v of applied.tags) params.append('label_id', v);
	for (const v of applied.requester) params.append('requester_id', v);
	for (const v of applied.slaState) params.append('sla_status', v);

	if (dateField === 'last_activity_at') {
		if (from) params.set('updated_after', from);
		if (to) params.set('updated_before', to);
	} else {
		// 'created_at' maps directly to created-date range.
		// 'resolved_at' has no backend filter input yet; falls back to created-date
		// range as a temporary approximation, matching buildDashboardTicketFilters.
		if (from) params.set('created_after', from);
		if (to) params.set('created_before', to);
	}

	for (const exportKey of toExportColumns(columnKeys)) {
		params.append('columns', exportKey);
	}

	if (filename) {
		params.set('filename', filename);
	}

	return params;
}

/** The full export URL for an already-serialized param set. */
export function dashboardExportUrl(params: URLSearchParams): string {
	return `${TICKET_EXPORT_PATH}?${params.toString()}`;
}

/**
 * Starts the streaming export by clicking a hidden download anchor.
 *
 * A download anchor rather than a `fetch`: fetching the file would buffer the
 * whole thing in memory, which is the size ceiling this export exists to remove.
 * And rather than a plain top-level navigation: the endpoint answers a failure
 * with a bare status and an empty body, so navigating to one would replace the
 * dashboard with a blank page. A download navigation leaves the SPA document
 * untouched either way, and the browser reports a failed download in its own UI.
 *
 * `filename` is a fallback only — the response carries a Content-Disposition
 * filename, which browsers honour ahead of this attribute. Both are given the
 * same name, so the file lands with that name whichever one wins.
 *
 * The anchor is removed immediately: the click has already handed the navigation
 * to the browser, which owns the download from that point on.
 */
export function triggerExportDownload(url: string, filename: string): void {
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	anchor.style.display = 'none';
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
}
